import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SimpleGrid, Checkbox, TextInput, Stack } from "@mantine/core";
import { useRiskControlSettings } from "../contexts/riskControlSettingsContext";
import {
  getEnabledSourceOfFundOptions,
  getIncomeSourceSpecifyField,
  normalizeSourceOfFundOptions,
} from "../helpers/riskControlSettingsUtils";

const ClientIncomeSource = ({ form: mainForm, disabled = false }) => {
  const { t } = useTranslation();
  const { sourceOfFundOptions } = useRiskControlSettings();

  if (!mainForm.values.incomeSources) {
    mainForm.values.incomeSources = {};
  }

  const allOptions = useMemo(
    () => normalizeSourceOfFundOptions(sourceOfFundOptions),
    [sourceOfFundOptions]
  );

  const enabledKeys = useMemo(
    () => new Set(getEnabledSourceOfFundOptions(sourceOfFundOptions).map((option) => option.key)),
    [sourceOfFundOptions]
  );

  const visibleOptions = useMemo(() => {
    const incomeSources = mainForm.values.incomeSources || {};
    return allOptions.filter(
      (option) => enabledKeys.has(option.key) || incomeSources[option.key]
    );
  }, [allOptions, enabledKeys, mainForm.values.incomeSources]);

  const specifyFields = useMemo(() => {
    const incomeSources = mainForm.values.incomeSources || {};
    return visibleOptions.filter(
      (option) => option.requiresSpecify && incomeSources[option.key]
    );
  }, [visibleOptions, mainForm.values.incomeSources]);

  const handleCheckboxChange = (key, checked) => {
    mainForm.setFieldValue(`incomeSources.${key}`, checked);
    if (!checked) {
      const specifyField = getIncomeSourceSpecifyField(key);
      mainForm.setFieldValue(`incomeSources.${specifyField}`, "");
    }
  };

  return (
    <Stack spacing="md">
      <SimpleGrid
        cols={3}
        spacing="sm"
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "sm", cols: 1 },
        ]}
      >
        {visibleOptions.map((option) => {
          const isReadOnly = disabled || !enabledKeys.has(option.key);
          const checkboxProps = mainForm.getInputProps(`incomeSources.${option.key}`, {
            type: "checkbox",
          });

          return (
            <Checkbox
              key={option.key}
              label={t(option.label)}
              disabled={isReadOnly}
              checked={Boolean(checkboxProps.checked)}
              onChange={(event) =>
                handleCheckboxChange(option.key, event.currentTarget.checked)
              }
            />
          );
        })}
      </SimpleGrid>

      {specifyFields.map((option) => (
        <TextInput
          key={option.key}
          label={`${t(option.label)}, ${t("please specify")}`}
          disabled={disabled}
          {...mainForm.getInputProps(
            `incomeSources.${getIncomeSourceSpecifyField(option.key)}`
          )}
        />
      ))}
    </Stack>
  );
};

export default ClientIncomeSource;
